import { ChartLump } from "../chart/types";
import { ColumnFilter } from "../display-table";
import { LumpColumnPredicate } from "../display-table/columnPredicateTypes";
import { assertNever } from "../errors";
import { HqlAggregationFunction } from "../hql";
import {
  getDatasetPathForField,
  joinDatasetQueryPath,
} from "../hql/hqlNextUtils";
import { WhereLumpOrderBy } from "../semantic-layer/generated/hex_sl_schema";

import { COUNT_STAR_ARG } from "./exploreConstants";
import {
  ExploreLumpOrderDirection,
  ExploreRankPredicate,
  ExploreRankPredicateOp,
} from "./exploreLumpTypes";
import {
  ExploreField,
  ExploreFieldId,
  ExploreFieldType,
  ExploreLump,
  ExploreSeriesId,
  ExploreSpec,
  ExploreVisualizationType,
} from "./types";

export const DEFAULT_LUMP_COMPARISON_VALUE = 10;
export const DEFAULT_LUMP_OTHER_LABEL = "Other";

export const lumpDirectionLabel = (
  direction: ExploreLumpOrderDirection | undefined,
): "Top" | "Bottom" | undefined => {
  return direction === "desc"
    ? "Top"
    : direction === "asc"
      ? "Bottom"
      : undefined;
};

export const getFieldLumpLabel = (
  lump: ExploreLump | ChartLump | undefined,
  withCount: boolean,
): string | undefined => {
  return lump == null
    ? undefined
    : withCount
      ? `${lumpDirectionLabel(lump.orderDirection)} ${lump.predicate.arg}`
      : lumpDirectionLabel(lump.orderDirection);
};

const rankPredicateOpToStr = (op: ExploreRankPredicateOp): string => {
  switch (op) {
    case "GT":
      return ">";
    case "GTE":
      return ">=";
    case "EQ":
      return "=";
    case "LTE":
      return "<=";
    case "LT":
      return "<";
    default:
      assertNever(op, op);
  }
};

export const getLumpPredicateString = (
  predicate: ExploreRankPredicate,
): string => {
  return `rank ${rankPredicateOpToStr(predicate.op)} ${predicate.arg}`;
};

export const lumpFilterPredicateToLump = (
  filterPredicate: LumpColumnPredicate,
): ExploreLump => {
  return {
    windowFunction: "row_number",
    orderDirection:
      filterPredicate.op === "TOP_N" || filterPredicate.op === "NOT_TOP_N"
        ? "desc"
        : "asc",
    predicate: {
      op:
        filterPredicate.op === "TOP_N" || filterPredicate.op === "BOTTOM_N"
          ? "LTE"
          : "GT",
      arg: Number(filterPredicate.arg),
    },
  };
};

export const lumpToLumpFilterPredicate = (
  lump: ExploreLump,
): LumpColumnPredicate => {
  const isDescending = lump.orderDirection === "desc";
  const isLTE = lump.predicate.op === "LTE";

  let op: LumpColumnPredicate["op"];
  if (isDescending && isLTE) {
    op = "TOP_N";
  } else if (isDescending && !isLTE) {
    op = "NOT_TOP_N";
  } else if (!isDescending && isLTE) {
    op = "BOTTOM_N";
  } else {
    op = "NOT_BOTTOM_N";
  }

  return {
    op,
    arg: String(lump.predicate.arg),
  };
};

export const invertLumpFilter = (
  filter: LumpColumnPredicate,
): LumpColumnPredicate => {
  let newOp: LumpColumnPredicate["op"];
  if (filter.op === "TOP_N") {
    newOp = "NOT_TOP_N";
  } else if (filter.op === "BOTTOM_N") {
    newOp = "NOT_BOTTOM_N";
  } else if (filter.op === "NOT_TOP_N") {
    newOp = "TOP_N";
  } else if (filter.op === "NOT_BOTTOM_N") {
    newOp = "BOTTOM_N";
  } else {
    assertNever(filter.op, filter.op);
  }
  return {
    op: newOp,
    arg: filter.arg,
  };
};

export const getDefaultOrderByField = (
  fields: ExploreField[],
  vizType: ExploreVisualizationType,
  seriesId: ExploreSeriesId | undefined,
): ExploreField | undefined => {
  const channel =
    vizType === "pivot-table"
      ? "value"
      : vizType === "chart"
        ? "cross-axis"
        : assertNever(vizType, vizType);
  return fields.find((f) => f.channel === channel && f.seriesId === seriesId);
};

export function resolveLumpOrderBy(
  lumpOrderBy: ExploreFieldId | undefined,
  seriesId: ExploreSeriesId | undefined,
  exploreSpec: ExploreSpec | undefined,
): WhereLumpOrderBy | undefined {
  if (exploreSpec == null) {
    return undefined;
  }

  const matchingField = exploreSpec.fields.find((f) => f.id === lumpOrderBy);
  const defaultField = getDefaultOrderByField(
    exploreSpec.fields,
    exploreSpec.visualizationType,
    seriesId ?? exploreSpec?.chartConfig.series?.[0]?.id, // if no series specified, default to the first series
  );

  const rankingField = matchingField ?? defaultField;

  if (rankingField?.value === COUNT_STAR_ARG) {
    return {
      expr: { fun: "count", args: [] },
      dataset: getDatasetPathForField(rankingField),
    };
  } else if (rankingField?.fieldType === ExploreFieldType.MEASURE) {
    return {
      measure: rankingField.value,
      dataset: getDatasetPathForField(rankingField),
    };
  } else if (rankingField) {
    // TODO: do we care about field type?
    const arg =
      exploreSpec.visualizationType === "chart"
        ? {
            fun: "_chart_tonumber" as const,
            args: [{ name: rankingField.value }],
          }
        : exploreSpec.visualizationType === "pivot-table"
          ? { name: rankingField.value }
          : assertNever(
              exploreSpec.visualizationType,
              exploreSpec.visualizationType,
            );

    return {
      expr: {
        fun: (
          rankingField.aggregation ?? "sum"
        ).toLowerCase() as Lowercase<HqlAggregationFunction>,
        args: [arg],
      },
      dataset: getDatasetPathForField(rankingField),
    };
  }
}

export const lumpFilterMatchesFieldLump = (
  filter: ColumnFilter,
  field: ExploreField,
): boolean => {
  if (field.lump == null) {
    return false;
  }
  const { arg, op } = lumpToLumpFilterPredicate(field.lump);
  return (
    LumpColumnPredicate.guard(filter.predicate) &&
    filter.column === field.value &&
    joinDatasetQueryPath(filter.queryPath ?? []) ===
      joinDatasetQueryPath(field.queryPath) &&
    filter.fieldType === field.fieldType &&
    filter.predicate.op === op &&
    filter.predicate.arg === arg &&
    filter.lumpOrderBy === field.lump.orderBy
  );
};
